import type { UseMutationResult } from 'react-query';
import { useMutation } from 'react-query';

import useCSRFToken from 'hooks/use-csrf-token';

import { createLocale } from 'locales';

import { showErrorToast, showSuccessToast } from 'components/toast/toast';

import type { Response } from '../types';

import client from '../client';

const localize = createLocale('freelancerAvailableJobsLocales');
const globalLocalize = createLocale('global');

type FreelancerAcceptJobRequest = {
  jobId: number;
};

const useFreelancerRefuseJob = (): UseMutationResult<
  Response,
  string,
  FreelancerAcceptJobRequest,
  unknown
> => {
  const csrfToken = useCSRFToken();
  const mutation = useMutation<
    Response,
    string,
    FreelancerAcceptJobRequest,
    unknown
  >(
    'block-freelancer-in-location',
    async (request: FreelancerAcceptJobRequest) => {
      const response = await fetch(`/api/job/${request.jobId}/refuse/`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        method: 'POST',
      });

      if (response.ok) return response.json();
      throw new Error('Something went wrong');
    },
    {
      onError: (err) => {
        showErrorToast({ description: err, title: globalLocalize('error') });
        client.invalidateQueries('get-available-single-call-jobs');
      },
      onSuccess: () => {
        showSuccessToast({
          description: localize('done'),
          title: globalLocalize('notification'),
        });
        client.invalidateQueries('get-available-single-call-jobs');
      },
    },
  );

  return mutation;
};

export default useFreelancerRefuseJob;
